// Render facade — the heart of QLIPPER-WEB. WebCodecs pipeline, proven end-to-end
// in the spike (transport+audio) and GATE B (1080x1920 reframe + real Satori overlay).
//
//   demux source ─► decode [start,end] frames ─► reframe to target aspect ─►
//   draw Satori hook PNG + karaoke subtitles ─► encode H.264 ─► mux (+copied AAC) ─► Blob
//
// opts: { sourceBlob, start, end, outputFormat, outputResolution, styleSettings,
//         hookText, wordTimings, onProgress, signal } -> Blob (MP4)

import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { demux, pickH264Codec, aacAudioSpecificConfig } from './mp4.js';
import { renderHookPNG } from './overlay.js';
import { drawSubtitleFrame } from './subtitles.js';

export function isWebCodecsSupported() {
  return 'VideoEncoder' in globalThis && 'VideoDecoder' in globalThis;
}

// Target dimensions per (format, resolution). Short side scales with resolution.
function targetDims(outputFormat, srcW, srcH, outputResolution) {
  const shortSide = outputResolution === '1080p' ? 1080
    : outputResolution === '720p' ? 720
    : outputResolution === '480p' ? 480 : 1080;

  switch (outputFormat) {
    case 'original':
    case 'raw-cuts': {
      // keep source aspect, scale so the SHORT side ~= shortSide, even dims
      const ar = srcW / srcH;
      let W, H;
      if (ar >= 1) { H = shortSide; W = Math.round(shortSide * ar); }
      else { W = shortSide; H = Math.round(shortSide / ar); }
      return { W: W - (W % 2), H: H - (H % 2), mode: 'contain' };
    }
    case 'sq-blur':
    case 'sq-zoom':
      return { W: shortSide, H: shortSide, mode: outputFormat === 'sq-blur' ? 'blur' : 'cover' };
    case 'stacked':
    case 'vertical-zoom':
    default:
      // 9:16 portrait — the Shorts/Reels default
      return { W: shortSide, H: Math.round(shortSide * 16 / 9), mode: outputFormat === 'sq-blur' ? 'blur' : 'cover' };
  }
}

// Draw one frame into a destination rect, applying the source's display rotation.
// srcW/srcH given to the callers below are DISPLAY dimensions (already swapped for
// 90/270), so every scale calculation stays in display space and is unchanged by
// this fix. Only the final blit rotates. See rotationFromMatrix() in mp4.js for why
// the decoder cannot do this for us.
export function blit(ctx, frame, dx, dy, dw, dh, rot) {
  if (!rot) { ctx.drawImage(frame, dx, dy, dw, dh); return; }
  const quarter = rot === 90 || rot === 270;
  // In the rotated frame of reference the destination box swaps its axes.
  const tw = quarter ? dh : dw;
  const th = quarter ? dw : dh;
  ctx.save();
  ctx.translate(dx + dw / 2, dy + dh / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(frame, -tw / 2, -th / 2, tw, th);
  ctx.restore();
}

// Draw the source frame into the target canvas per reframe mode.
function drawReframe(ctx, frame, srcW, srcH, TW, TH, mode, rot = 0) {
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, TW, TH);
  if (mode === 'contain') {
    const scale = Math.min(TW / srcW, TH / srcH);
    const dw = srcW * scale, dh = srcH * scale;
    blit(ctx, frame, (TW - dw) / 2, (TH - dh) / 2, dw, dh, rot);
  } else if (mode === 'blur') {
    // fill background with a zoomed, blurred copy, then the contained frame on top
    const cover = Math.max(TW / srcW, TH / srcH);
    ctx.save();
    ctx.filter = 'blur(24px) brightness(0.6)';
    blit(ctx, frame, (TW - srcW * cover) / 2, (TH - srcH * cover) / 2, srcW * cover, srcH * cover, rot);
    ctx.restore();
    const fit = Math.min(TW / srcW, TH / srcH);
    const dw = srcW * fit, dh = srcH * fit;
    blit(ctx, frame, (TW - dw) / 2, (TH - dh) / 2, dw, dh, rot);
  } else {
    // cover: center-crop to fill
    const scale = Math.max(TW / srcW, TH / srcH);
    const dw = srcW * scale, dh = srcH * scale;
    blit(ctx, frame, (TW - dw) / 2, (TH - dh) / 2, dw, dh, rot);
  }
}

let _fontsCache = null;
async function loadFonts() {
  if (_fontsCache) return _fontsCache;
  const load = async (url, name, weight) => {
    try { return { name, weight, style: 'normal', data: await (await fetch(url)).arrayBuffer() }; }
    catch { return null; }
  };
  const fonts = (await Promise.all([
    load('/fonts/THEBOLDFONT-FREEVERSION.ttf', 'TheBold', 700),
    load('/fonts/KOMIKAX_.ttf', 'Komika', 400),
  ])).filter(Boolean);
  _fontsCache = fonts;
  return fonts;
}

export async function renderClip(opts) {
  const {
    sourceBlob, start = 0, end, outputFormat = 'vertical-zoom',
    outputResolution = '1080p', styleSettings = {}, hookText = '',
    wordTimings = null, onProgress, signal,
  } = opts;
  if (!isWebCodecsSupported()) throw new Error('WebCodecs not supported in this browser');

  const ab = await sourceBlob.arrayBuffer();
  const { video, audio } = await demux(ab);
  if (!video.description) throw new Error('Could not read source video codec description');

  // Clamp clip times to the actual video duration — the AI can return start/end past
  // the end of the source (esp. without a transcript), which would yield zero frames.
  const dur = video.durationSec || (end ?? 0);
  const clipStart = Math.max(0, Math.min(start, Math.max(0, dur - 0.2)));
  const clipEnd = Math.min(end ?? dur, dur);
  if (clipEnd - clipStart < 0.1) {
    throw new Error(`clip ${start}-${end}s is outside the ${dur.toFixed(1)}s video`);
  }
  const startUs = clipStart * 1e6;
  const endUs = clipEnd * 1e6;

  // Display dimensions: a 90/270 display matrix means stored width/height are
  // swapped relative to what a viewer sees. Target sizing and every reframe
  // calculation below must work in display space, or a portrait source gets
  // framed as if it were landscape.
  const srcRot = video.rotation || 0;
  const srcQuarter = srcRot === 90 || srcRot === 270;
  const dispW = srcQuarter ? video.height : video.width;
  const dispH = srcQuarter ? video.width : video.height;

  const { W: TW, H: TH, mode } = targetDims(outputFormat, dispW, dispH, outputResolution);
  const codec = await pickH264Codec(TW, TH);
  if (!codec) throw new Error(`No H.264 codec supports ${TW}x${TH}`);

  // Hook overlay PNG (Satori) — rendered once, drawn over every frame.
  const fonts = await loadFonts();
  let overlayBmp = null;
  if (styleSettings.hookEnabled !== false && hookText) {
    const png = await renderHookPNG({ text: hookText, W: TW, H: TH, style: styleSettings, fonts });
    overlayBmp = await createImageBitmap(png);
  }

  // Subtitle timings rebased to clip-local seconds.
  const subEnabled = styleSettings.subtitleEnabled !== false && Array.isArray(wordTimings) && wordTimings.length > 0;
  const localTimings = subEnabled
    ? wordTimings
        .filter((w) => w.end > clipStart && w.start < clipEnd)
        .map((w) => ({ word: w.word, start: Math.max(0, w.start - clipStart), end: w.end - clipStart }))
    : [];

  const canvas = new OffscreenCanvas(TW, TH);
  const ctx = canvas.getContext('2d');

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: { codec: 'avc', width: TW, height: TH },
    ...(audio ? { audio: { codec: 'aac', sampleRate: audio.sampleRate, numberOfChannels: audio.channels } } : {}),
    fastStart: 'in-memory',
    // tolerate a near-zero first timestamp instead of throwing (belt-and-suspenders)
    firstTimestampBehavior: 'offset',
  });

  const encoder = new VideoEncoder({ output: (c, m) => muxer.addVideoChunk(c, m), error: (e) => { throw e; } });
  encoder.configure({ codec, width: TW, height: TH, bitrate: outputResolution === '480p' ? 2_500_000 : 6_000_000, framerate: 30 });

  // Decode: start from the keyframe at/just before clipStart; only encode frames in range.
  let firstKeyIdx = 0;
  for (let i = 0; i < video.samples.length; i++) {
    if (video.samples[i].timestamp <= startUs && video.samples[i].isKey) firstKeyIdx = i;
    if (video.samples[i].timestamp > startUs) break;
  }

  let emitted = 0;
  let baseTs = null;
  const totalInRange = video.samples.filter((s) => s.timestamp >= startUs && s.timestamp < endUs).length || 1;

  const decoder = new VideoDecoder({
    output: (frame) => {
      const ts = frame.timestamp;
      if (ts < startUs || ts >= endUs) { frame.close(); return; }
      if (baseTs === null) baseTs = ts;
      const localTs = ts - baseTs;
      drawReframe(ctx, frame, dispW, dispH, TW, TH, mode, srcRot);
      if (overlayBmp) ctx.drawImage(overlayBmp, 0, 0);
      if (localTimings.length) drawSubtitleFrame(ctx, localTimings, localTs / 1e6, { W: TW, H: TH, style: styleSettings });
      const out = new VideoFrame(canvas, { timestamp: localTs, duration: frame.duration || 33333 });
      encoder.encode(out, { keyFrame: emitted % 60 === 0 });
      out.close(); frame.close();
      emitted++;
      if (onProgress && emitted % 10 === 0) onProgress(Math.min(0.95, emitted / totalInRange));
    },
    error: (e) => { throw e; },
  });
  decoder.configure({ codec: video.codec, description: video.description });

  for (let i = firstKeyIdx; i < video.samples.length; i++) {
    if (signal?.aborted) throw new Error('Render aborted');
    const s = video.samples[i];
    if (s.timestamp >= endUs) break;
    decoder.decode(new EncodedVideoChunk({
      type: s.isKey ? 'key' : 'delta', timestamp: s.timestamp, duration: s.duration, data: s.data,
    }));
  }
  await decoder.flush();
  await encoder.flush();

  // No frames encoded => the muxer has no video decoderConfig and finalize() crashes
  // reading colorSpace. Fail cleanly instead (caught per-clip by the render loop).
  if (emitted === 0) throw new Error('no frames rendered for clip range');

  // Audio: copy AAC frames in [start,end]. Rebase to the FIRST INCLUDED audio sample
  // (not startUs) so the first chunk is exactly timestamp 0 — the muxer rejects a
  // non-zero first timestamp. (startUs rarely lands on an audio frame boundary, so
  // any clip not starting at 0 would otherwise crash.)
  if (audio) {
    const desc = aacAudioSpecificConfig(audio.sampleRate, audio.channels);
    let first = true;
    let audioBase = null;
    for (const s of audio.samples) {
      if (s.timestamp < startUs || s.timestamp >= endUs) continue;
      if (audioBase === null) audioBase = s.timestamp;
      const chunk = new EncodedAudioChunk({
        type: 'key', timestamp: s.timestamp - audioBase, duration: s.duration, data: s.data,
      });
      muxer.addAudioChunk(chunk, first ? { decoderConfig: { description: desc } } : undefined);
      first = false;
    }
  }

  muxer.finalize();
  if (onProgress) onProgress(1);
  return new Blob([target.buffer], { type: 'video/mp4' });
}
